import React, { useEffect, useId, useState } from 'react'
import {
  Button,
  Checkbox,
  InputField,
  RadioGroup,
  Select,
  Tooltip,
} from '@enamelai/design-system'
import {
  FORM_COMPONENTS,
  COMPONENT_TOOLTIPS,
  SHOW_ATTACHMENTS_AND_ADMIN_SIGNATURE_COMPONENTS,
  formatFormComponentDisplayLabel,
  INPUT_FIELD_AUTOFILL_OPTIONS,
  DATE_FIELD_AUTOFILL_OPTIONS,
  GENDER_SELECTION_AUTOFILL_OPTIONS,
} from '../constants'
import type {
  FieldSettingsUpdateHandler,
  FormBuilderSelection,
  FormComponentType,
  FormRowColumnCount,
  PatientInfoField,
} from '../types'
import { editableOptionsForField } from '../createFormField'
import './FormComponentsSidebar.css'

const VISIBLE_FORM_COMPONENTS = FORM_COMPONENTS.filter(
  (c) =>
    SHOW_ATTACHMENTS_AND_ADMIN_SIGNATURE_COMPONENTS ||
    (c !== 'Attachments' && c !== 'Dentist/Admin Signature'),
)

const COMPONENT_ICONS: Record<string, string> = {
  'Group Fields': 'mgc_vector_group_line',
  'Form Row': 'mgc_rows_2_fill',
  'Input Field': 'mgc_textbox_line',
  'Text Area': 'mgc_paragraph_line',
  'Date Field': 'mgc_calendar_2_fill',
  Checkbox: 'mgc_checkbox_fill',
  'Checkbox Group': 'mgc_list_check_fill',
  Dropdown: 'mgc_selector_vertical_line',
  'Radio Group': 'mgc_radiobox_fill',
  'Rich Text': 'mgc_text_color_fill',
  'Gender Selection': 'mgc_radiobox_fill',
  Attachments: 'mgc_attachment_2_line',
  'Signature Field': 'mgc_signature_line',
  'Dentist/Admin Signature': 'mgc_signature_line',
}

interface FormComponentsSidebarProps {
  onComponentClick?: (component: FormComponentType) => void
  selectedField?: FormBuilderSelection | null
  onFieldSettingsChange?: FieldSettingsUpdateHandler
  readOnly?: boolean
}

const FormComponentsSidebar: React.FC<FormComponentsSidebarProps> = ({
  onComponentClick,
  selectedField,
  onFieldSettingsChange,
  readOnly = false,
}) => {
  const [activeTab, setActiveTab] = useState<'components' | 'settings'>(
    'components',
  )
  const sidebarId = useId()
  const tabs = [
    { id: 'components', label: 'Components' },
    { id: 'settings', label: 'Field Settings' },
  ] as const
  const selectedFieldId = selectedField?.id ?? null
  const [label, setLabel] = useState('')
  const [placeholder, setPlaceholder] = useState('')
  const [required, setRequired] = useState(false)
  const [autoFillSource, setAutoFillSource] = useState<PatientInfoField | null>(
    null,
  )
  const [options, setOptions] = useState<string[]>([])
  const [columnCount, setColumnCount] = useState<FormRowColumnCount>(2)

  useEffect(() => {
    if (selectedFieldId) {
      setActiveTab('settings')
    }
  }, [selectedFieldId])

  const isFormRow =
    selectedField != null &&
    selectedField.type === 'Form Row' &&
    'fields' in selectedField

  useEffect(() => {
    if (
      selectedField != null &&
      selectedField.type === 'Form Row' &&
      'fields' in selectedField
    ) {
      setColumnCount(selectedField.columnCount === 1 ? 1 : 2)
      return
    }
    const field =
      selectedField != null && !('fields' in selectedField)
        ? selectedField
        : null
    setLabel(field?.label ?? '')
    setPlaceholder(field?.placeholder ?? '')
    setRequired(field?.required ?? false)
    setAutoFillSource(field?.autoFillSource ?? null)
    const nextOptions = field ? editableOptionsForField(field) : []
    setOptions((current) =>
      current.length === nextOptions.length &&
      current.every((item, index) => item === nextOptions[index])
        ? current
        : nextOptions,
    )
  }, [selectedField])

  const getAutofillOptions = (): Array<{
    value: PatientInfoField | null
    label: string
  }> => {
    switch (selectedField?.type) {
      case 'Input Field':
      case 'Text Area':
        return INPUT_FIELD_AUTOFILL_OPTIONS
      case 'Date Field':
        return DATE_FIELD_AUTOFILL_OPTIONS
      case 'Gender Selection':
      case 'Radio Group':
        return GENDER_SELECTION_AUTOFILL_OPTIONS
      default:
        return []
    }
  }

  const supportsRequired =
    selectedField != null &&
    [
      'Input Field',
      'Text Area',
      'Date Field',
      'Dropdown',
      'Checkbox Group',
      'Radio Group',
      'Gender Selection',
    ].includes(selectedField.type)
  const supportsAutofill =
    selectedField != null &&
    [
      'Input Field',
      'Text Area',
      'Date Field',
      'Radio Group',
      'Gender Selection',
    ].includes(selectedField.type)
  const supportsChoiceOptions =
    selectedField != null &&
    ['Checkbox Group', 'Dropdown', 'Radio Group', 'Gender Selection'].includes(
      selectedField.type,
    )
  const supportsPlaceholder =
    selectedField != null &&
    [
      'Input Field',
      'Text Area',
      'Date Field',
      'Dropdown',
      'Rich Text',
    ].includes(selectedField.type)

  const updateLabel = (nextLabel: string): void => {
    setLabel(nextLabel)
    if (!selectedField) return
    onFieldSettingsChange?.(selectedField.id, { label: nextLabel })
  }

  const updatePlaceholder = (nextPlaceholder: string): void => {
    setPlaceholder(nextPlaceholder)
    if (!selectedField) return
    onFieldSettingsChange?.(selectedField.id, { placeholder: nextPlaceholder })
  }

  const updateOptions = (nextOptions: string[]): void => {
    setOptions(nextOptions)
    if (!selectedField) return
    onFieldSettingsChange?.(selectedField.id, { options: nextOptions })
  }

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void => {
    let nextIndex: number
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % tabs.length
        break
      case 'ArrowLeft':
        nextIndex = (index + tabs.length - 1) % tabs.length
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = tabs.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    setActiveTab(tabs[nextIndex].id)
    event.currentTarget.parentElement
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      [nextIndex]?.focus()
  }

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    component: FormComponentType,
  ): void => {
    if (readOnly) return
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({ type: component }),
    )
    e.dataTransfer.effectAllowed = 'copy'
    e.currentTarget.classList.add('dragging')
  }

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
    e.currentTarget.classList.remove('dragging')
  }

  const handleClick = (component: FormComponentType): void => {
    if (readOnly) return
    if (onComponentClick) {
      onComponentClick(component)
    }
  }

  return (
    <div
      className={`create-form-sidebar-right${
        readOnly ? ' create-form-sidebar-right-readonly' : ''
      }`}
    >
      <div
        className="form-sidebar-tabs"
        role="tablist"
        aria-label="Field tools"
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${sidebarId}-${tab.id}-tab`}
            aria-controls={`${sidebarId}-${tab.id}-panel`}
            aria-selected={activeTab === tab.id}
            tabIndex={activeTab === tab.id ? 0 : -1}
            className="form-sidebar-tab"
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`${sidebarId}-components-panel`}
        aria-labelledby={`${sidebarId}-components-tab`}
        hidden={activeTab !== 'components'}
        tabIndex={0}
        className="form-sidebar-panel"
      >
        <div className="components-list">
          {VISIBLE_FORM_COMPONENTS.map((component, index) => {
            const iconClass = COMPONENT_ICONS[component] || 'mgc_dots_fill'
            const isAdminSignature = component === 'Dentist/Admin Signature'
            const tooltip = COMPONENT_TOOLTIPS[component]

            return (
              <Tooltip
                key={index}
                content={tooltip}
                placement="left"
                className="component-tooltip"
                portal
              >
                <div
                  className="component-box"
                  draggable={!readOnly}
                  onDragStart={(e) =>
                    handleDragStart(e, component as FormComponentType)
                  }
                  onDragEnd={handleDragEnd}
                  onClick={() => handleClick(component as FormComponentType)}
                >
                  <div
                    className={`component-box-inner ${isAdminSignature ? 'admin-signature' : ''}`}
                  >
                    <i className="mgc mgc_dots_fill component-drag-handle"></i>
                    <i className={`mgc ${iconClass}`}></i>
                    <span>{formatFormComponentDisplayLabel(component)}</span>
                  </div>
                </div>
              </Tooltip>
            )
          })}
        </div>
      </div>
      <div
        role="tabpanel"
        id={`${sidebarId}-settings-panel`}
        aria-labelledby={`${sidebarId}-settings-tab`}
        hidden={activeTab !== 'settings'}
        tabIndex={0}
        className="form-sidebar-panel"
      >
        {selectedField ? (
          <div className="field-settings-form">
            <div className="field-settings-heading">
              <div className="field-settings-heading-title">
                <i
                  className={`mgc ${COMPONENT_ICONS[selectedField.type] || 'mgc_dots_fill'}`}
                  aria-hidden="true"
                ></i>
                <strong>
                  {formatFormComponentDisplayLabel(selectedField.type)}
                </strong>
              </div>
              <span>Configure the selected field</span>
            </div>

            {isFormRow && (
              <RadioGroup
                name="form-row-column-count"
                label="Fields in row"
                value={String(columnCount)}
                onChange={(value) => {
                  const nextCount: FormRowColumnCount = value === '1' ? 1 : 2
                  setColumnCount(nextCount)
                  onFieldSettingsChange?.(selectedField.id, {
                    columnCount: nextCount,
                  })
                }}
                options={[
                  { value: '1', label: '1 field' },
                  { value: '2', label: '2 fields' },
                ]}
              />
            )}

            {selectedField.type !== 'Rich Text' && !isFormRow && (
              <InputField
                variant="text"
                label="Label"
                value={label}
                placeholder={selectedField.type}
                fullWidth
                onChange={(event) => updateLabel(event.target.value)}
                onClear={() => updateLabel('')}
              />
            )}

            {supportsPlaceholder && selectedField.type === 'Dropdown' && (
              <InputField
                variant="text"
                label="Placeholder"
                value={placeholder}
                placeholder="Select"
                fullWidth
                onChange={(event) => updatePlaceholder(event.target.value)}
                onClear={() => updatePlaceholder('')}
              />
            )}

            {supportsPlaceholder &&
              selectedField.type !== 'Dropdown' &&
              (selectedField.type === 'Rich Text' ||
                selectedField.type === 'Text Area') && (
                <InputField
                  variant="message"
                  label={
                    selectedField.type === 'Rich Text'
                      ? 'Content'
                      : 'Placeholder'
                  }
                  value={placeholder}
                  rows={5}
                  placeholder={
                    selectedField.type === 'Rich Text'
                      ? 'Enter content'
                      : 'Set field placeholder'
                  }
                  maxLength={10000}
                  showCounter={false}
                  fullWidth
                  onChange={(event) => updatePlaceholder(event.target.value)}
                />
              )}

            {supportsPlaceholder &&
              selectedField.type !== 'Dropdown' &&
              selectedField.type !== 'Rich Text' &&
              selectedField.type !== 'Text Area' && (
                <InputField
                  variant="text"
                  label="Placeholder"
                  value={placeholder}
                  placeholder="Set field placeholder"
                  fullWidth
                  onChange={(event) => updatePlaceholder(event.target.value)}
                  onClear={() => updatePlaceholder('')}
                />
              )}

            {supportsChoiceOptions && (
              <div className="field-settings-options">
                <span>Choices</span>
                {options.map((option, index) => (
                  <div
                    key={`option-${index}`}
                    className="field-settings-option-row"
                  >
                    <InputField
                      variant="text"
                      value={option}
                      label=""
                      aria-label={`Choice ${index + 1}`}
                      fullWidth
                      clearable={false}
                      onChange={(event) => {
                        const nextOptions = options.map((item, itemIndex) =>
                          itemIndex === index ? event.target.value : item,
                        )
                        updateOptions(nextOptions)
                      }}
                    />
                    <button
                      type="button"
                      className="field-settings-option-remove"
                      aria-label={`Remove choice ${index + 1}`}
                      disabled={options.length <= 1}
                      onClick={() => {
                        updateOptions(
                          options.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }}
                    >
                      <i
                        className="mgc mgc_delete_2_fill"
                        aria-hidden="true"
                      ></i>
                    </button>
                  </div>
                ))}
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => {
                    updateOptions([...options, `Option ${options.length + 1}`])
                  }}
                >
                  Add choice
                </Button>
              </div>
            )}

            {supportsRequired && (
              <Checkbox
                label="Required"
                checked={required}
                onChange={(event) => {
                  const nextRequired = event.target.checked
                  setRequired(nextRequired)
                  onFieldSettingsChange?.(selectedField.id, {
                    required: nextRequired,
                  })
                }}
              />
            )}

            {supportsAutofill && (
              <Select
                label="Auto-fill from patient"
                placeholder="None"
                value={autoFillSource ?? ''}
                options={getAutofillOptions().map((option) => ({
                  value: option.value ?? '',
                  label: option.label,
                }))}
                onChange={(value) => {
                  const nextSource =
                    ((Array.isArray(value) ? value[0] : value) as
                      | PatientInfoField
                      | ''
                      | undefined) || null
                  const option = getAutofillOptions().find(
                    (item) => item.value === nextSource,
                  )
                  setAutoFillSource(nextSource)
                  if (option && nextSource) {
                    setLabel(option.label)
                  }
                  onFieldSettingsChange?.(selectedField.id, {
                    autoFillSource: nextSource,
                    ...(option && nextSource ? { label: option.label } : {}),
                  })
                }}
              />
            )}
          </div>
        ) : (
          <div className="field-settings-empty-state">
            <i className="mgc mgc_settings_3_line" aria-hidden="true"></i>
            <p>Select a field to configure</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default FormComponentsSidebar
